import { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '@material-ui/core/Icon';
import { apiClient } from '@services/api';

const POLL_INTERVAL = 30000; // 30s — mesmo intervalo do RefundStatusIcon / NotificationBell

const brl = v =>
  v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = iso => {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

const fmtDateTime = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const daysLeft = iso => {
  if (!iso) return null;
  const end = new Date(`${String(iso).slice(0, 10)}T23:59:59`);
  return Math.ceil((end - new Date()) / 86400000);
};

const FRONT_LABEL = { app: 'App', presale: 'Orçamento (presale)', site: 'Site', signup: 'Cadastro (site)' };

const REASON_LABEL = {
  cpf_invalido: 'CPF inválido',
  ip_muitas_tentativas: 'IP com muitas tentativas',
  ip_varios_cartoes: 'IP testando vários cartões',
  ip_recusas: 'IP com muitas recusas',
  email_muitas_tentativas: 'E-mail com muitas tentativas',
  cartao_muitas_tentativas: 'Cartão com muitas tentativas',
  cartao_varios_emails: 'Mesmo cartão em vários e-mails',
  turnstile_falhou: 'CAPTCHA não validado',
  email_descartavel: 'E-mail descartável',
  email_padrao_suspeito: 'E-mail com padrão suspeito',
  telefone_pais_divergente: 'Telefone de outro país x celular BR',
  compra_madrugada_pos_cadastro: 'Compra de madrugada logo após cadastro',
  dados_divergem_cadastro: 'Dados da compra diferem do cadastro',
  ligado_conta_bloqueada: 'Mesmo CPF/WhatsApp de conta bloqueada',
  cep_invalido: 'CEP inválido',
  ip_mudou_apos_login: 'IP mudou após o login',
  ip_muitos_cadastros: 'IP com muitos cadastros',
  turnstile: 'CAPTCHA não validado',
};

// turnstile_causa:<causa_servidor>|<estado_navegador> — separa falha técnica de rejeição real
const CAUSE_LABEL = {
  sem_token: 'sem token',
  desconhecida: 'causa desconhecida',
  script_bloqueado: 'script bloqueado no navegador',
  timeout: 'tempo esgotado',
  erro_widget: 'erro do widget',
  erro_render: 'erro ao iniciar',
  sem_chave: 'sem chave no front',
  nao_executado: 'não executou',
  ok: 'navegador gerou token',
};
const causeText = r => {
  const [server, client] = String(r).replace('turnstile_causa:', '').split('|');
  const s = server.startsWith('rejeitado:')
    ? `rejeitado pelo Cloudflare (${server.slice(10)})`
    : CAUSE_LABEL[server] || server;
  return `CAPTCHA: ${s}${client ? ` · ${CAUSE_LABEL[client] || client}` : ''}`;
};
const reasonText = list =>
  (list || []).map(r => (String(r).startsWith('turnstile_causa:') ? causeText(r) : REASON_LABEL[r] || r)).join(', ');

/**
 * Ponto de exclamação ao lado do ícone de reembolso.
 * - Contestações (chargebacks) abertas nas contas Asaas 1 e 2 (2026-10-08, caso Thales).
 * - Antifraude de cartão (2026-10-08): tentativas liberadas x barradas nas últimas 24h e 7 dias,
 *   e as últimas tentativas barradas com o motivo (app, presale e site).
 * Cores: vermelho = contestação aberta · âmbar = houve tentativa barrada nas últimas 24h ·
 * verde = só tentativas liberadas.
 * Fonte: GET /chargebacks/status (chargebacks: webhook + app:check-asaas-chargebacks;
 * antifraude: tabela card_attempts).
 */
export default function ChargebackAlertIcon() {
  const [open, setOpen] = useState(0);
  const [items, setItems] = useState([]);
  const [af, setAf] = useState(null);
  const [review, setReview] = useState([]);
  const [releases, setReleases] = useState([]);
  const [canRelease, setCanRelease] = useState(false);
  const [deciding, setDeciding] = useState(null);
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  const fetchStatus = useCallback(() => {
    apiClient.httpInstance
      .get('/chargebacks/status')
      .then(res => {
        if (res.status === 200) {
          setOpen(res.data?.open ?? 0);
          setItems(res.data?.items ?? []);
          setAf(res.data?.antifraud ?? null);
          setReview(res.data?.review ?? []);
          setReleases(res.data?.releases ?? []);
          setCanRelease(!!res.data?.can_release);
        }
      })
      .catch(() => {});
  }, []);

  // Aprovar/recusar pedido de liberação de cartão do site (2026-10-19)
  const decideRelease = (id, action) => {
    const ok = window.confirm(
      action === 'approve'
        ? 'Liberar o cartão para esta compra? O cliente recebe um e-mail para finalizar o pagamento.'
        : 'Recusar o cartão para esta compra? O cliente só poderá pagar no Pix.'
    );
    if (!ok) return;
    setDeciding(id);
    apiClient.httpInstance
      .post(`/antifraud/releases/${id}/${action}`)
      .then(() => fetchStatus())
      .catch(() => window.alert('Não foi possível registrar a decisão. Tente novamente.'))
      .finally(() => setDeciding(null));
  };

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchStatus]);

  // fecha ao clicar fora
  useEffect(() => {
    if (!show) return undefined;
    const onDoc = e => {
      if (ref.current && !ref.current.contains(e.target)) setShow(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [show]);

  const d24 = af?.last_24h;
  const d7 = af?.last_7d;
  const recent = af?.recent_blocked ?? [];
  const blocked24 = d24?.blocked ?? 0;
  const hasAfActivity = (d7?.total ?? 0) > 0;

  // Sem contestação e sem nenhuma verificação nos últimos 7 dias: nada para mostrar
  if (open <= 0 && !hasAfActivity && review.length === 0 && releases.length === 0) return null;

  // Só contestação AGUARDANDO resposta deixa o ícone vermelho/pulsando. Depois de respondida
  // (status da disputa no Asaas deixa de ser REQUESTED) ela continua listada, mas o ícone volta
  // às cores normais (âmbar se houve barradas em 24h, senão verde).
  // Disputa perdida (DISPUTE_LOST) também chama atenção (vermelho).
  const pending = items.filter(c => c.needs_response !== false || c.lost).length;
  // Pedido de liberação de cartão aguardando decisão: cor própria (roxo) pulsando, para não confundir com contestação (vermelho)
  const color = releases.length > 0 ? '#a855f7' : pending > 0 ? '#ef4444' : blocked24 > 0 || review.length > 0 ? '#f59e0b' : '#62DEC2';
  const pulse = pending > 0 || releases.length > 0;
  const badge = pending + blocked24 + review.length + releases.length;

  const copySummary = () => {
    const lines = [];
    lines.push(`Antifraude — 24h: ${d24?.allowed ?? 0} liberadas, ${blocked24} barradas | 7 dias: ${d7?.allowed ?? 0} liberadas, ${d7?.blocked ?? 0} barradas`);
    (af?.turnstile ?? []).forEach(t => {
      lines.push(`CAPTCHA 7d [${FRONT_LABEL[t.front] || t.front}] ${t.ok}/${t.total} ok, ${t.fail} falhou, ${t.skipped} sem`);
    });
    recent.forEach(a => {
      lines.push(
        `BARRADA ${fmtDateTime(a.created_at)} [${FRONT_LABEL[a.front] || a.front}] ${reasonText(a.reasons)} | ${a.email || 'sem e-mail'} | IP ${a.ip || '—'} | cartão ${a.card || '—'} | ref ${a.ref || '—'} | score ${a.score}`
      );
    });
    releases.forEach(r => {
      lines.push(
        `LIBERAÇÃO PENDENTE fatura ${r.ref} ${r.customer_name || '?'} | ${r.email || 'sem e-mail'} | ${r.phone || 'sem telefone'} | hotel ${r.hotel || '—'} | passeio ${fmtDate(r.tour_date) || '—'} | ${brl(r.amount)} | pedido ${fmtDateTime(r.requested_at)}`
      );
    });
    review.forEach(p => {
      lines.push(
        `REVISAR ${p.sale_reference || ''} ${p.customer_name || '?'} | ${p.email || 'sem e-mail'} | ${p.phone || 'sem telefone'} | hotel ${p.hotel || '—'} | ${p.tour_name || 'tour'} em ${fmtDate(p.tour_date) || '—'} (${p.people} pessoa(s)) | ${brl(p.total)} | comprado ${fmtDateTime(p.purchased_at)}`
      );
    });
    items.forEach(c => {
      lines.push(`CHARGEBACK ${c.needs_response === false ? '[respondida]' : '[aguardando resposta]'} ${c.customer_name || '?'} ${brl(c.value)} ${c.sale_reference || ''} conta ${c.account} prazo ${fmtDate(c.deadline) || '—'} motivo ${c.reason || '—'}`);
    });
    const text = lines.join('\n');
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => {});
    }
  };

  const sectionTitle = (text, tone) => ({
    padding: '10px 12px',
    fontWeight: 700,
    borderBottom: '1px solid #1e3a5f',
    color: tone,
  });

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <style>{`@keyframes ftChargebackPulse { 0%,100% { opacity: 1 } 50% { opacity: .45 } }`}</style>
      <button
        onClick={() => setShow(s => !s)}
        title={
          pending > 0
            ? `${pending} contestação(ões) aguardando resposta no Asaas — clique para ver`
            : open > 0
            ? `${open} contestação(ões) respondida(s), em análise no Asaas — clique para ver`
            : `Antifraude: ${d24?.allowed ?? 0} liberadas e ${blocked24} barradas nas últimas 24h — clique para ver`
        }
        style={{
          position: 'relative',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '6px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Icon style={{ fontSize: '24px', color, animation: pulse ? 'ftChargebackPulse 1.2s infinite' : 'none' }}>
          {pending > 0 || blocked24 > 0 || review.length > 0 || releases.length > 0 ? 'priority_high' : 'verified_user'}
        </Icon>
        {badge > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              minWidth: '16px',
              height: '16px',
              borderRadius: '8px',
              background: color,
              color: '#fff',
              fontSize: '10px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 3px',
              lineHeight: 1,
            }}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>

      {show && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '6px',
            width: '380px',
            maxHeight: '520px',
            overflowY: 'auto',
            background: '#0f2340',
            border: `1px solid ${color}`,
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,.45)',
            zIndex: 1000,
            color: '#e6eefb',
            fontSize: '13px',
          }}
        >
          {open > 0 && (
            <>
              <div style={sectionTitle('', pending > 0 ? '#fca5a5' : '#62DEC2')}>
                {pending > 0 ? '❗ Contestações no Asaas' : '✅ Contestação respondida — em análise'}
              </div>
              {items.map(c => {
                const left = daysLeft(c.deadline);
                const answered = c.needs_response === false && !c.lost;
                // Valores do Asaas (docs.asaas.com/docs/chargeback): REQUESTED, IN_DISPUTE, DISPUTE_LOST, REVERSED, DONE
                const statusLabel = c.lost
                  ? 'Disputa perdida (DISPUTE_LOST)'
                  : c.dispute_status === 'IN_DISPUTE'
                  ? 'Em disputa (IN_DISPUTE) — aguardando o banco'
                  : `Respondida (${c.dispute_status}) — aguardando o banco`;
                return (
                  <div key={c.id} style={{ padding: '10px 12px', borderBottom: '1px solid #16304f' }}>
                    <div style={{ fontWeight: 600 }}>{c.customer_name || 'Cliente não identificado'}</div>
                    <div style={{ color: answered ? '#62DEC2' : '#fca5a5', fontSize: '12px', fontWeight: 600 }}>
                      {c.lost || !answered ? (c.lost ? statusLabel : 'Aguardando sua resposta') : statusLabel}
                    </div>
                    <div style={{ color: '#8ba5c4' }}>
                      {brl(c.value)} · {c.sale_reference || 'venda não identificada'} · conta {c.account}
                    </div>
                    {c.reason && <div style={{ color: '#fca5a5', marginTop: '2px' }}>Motivo: {c.reason}</div>}
                    {c.deadline && !answered && (
                      <div style={{ marginTop: '2px', color: left !== null && left <= 3 ? '#f87171' : '#fcd34d' }}>
                        Prazo para contestar: {fmtDate(c.deadline)}
                        {left !== null ? (left >= 0 ? ` (${left} dia(s))` : ' (vencido)') : ''}
                      </div>
                    )}
                    <div style={{ color: '#6b87a8', marginTop: '2px', fontSize: '11px' }}>{c.payment_id}</div>
                  </div>
                );
              })}
              <div style={{ padding: '8px 12px', color: '#8ba5c4', fontSize: '11px', borderBottom: '1px solid #1e3a5f' }}>
                {pending > 0
                  ? 'Para contestar, abra a cobrança no painel do Asaas (Cobranças → Chargeback) da conta indicada.'
                  : 'Resposta enviada. O alerta some quando o Asaas encerrar a contestação.'}
              </div>
            </>
          )}

          {releases.length > 0 && (
            <>
              <div style={sectionTitle('', '#d8b4fe')}>🔐 Aguardando liberação — cartão (site)</div>
              {releases.map(r => (
                <div key={r.id} style={{ padding: '10px 12px', borderBottom: '1px solid #16304f' }}>
                  <div style={{ fontWeight: 600 }}>{r.customer_name || 'Cliente não identificado'}</div>
                  <div style={{ color: '#8ba5c4' }}>
                    Fatura {r.ref} · {brl(r.amount)}
                  </div>
                  <div style={{ color: '#fcd34d' }}>
                    Passeio em {fmtDate(r.tour_date) || '—'} · pedido {fmtDateTime(r.requested_at)}
                  </div>
                  <div style={{ color: '#8ba5c4', fontSize: '12px' }}>
                    {r.email || 'sem e-mail'} · {r.phone || 'sem telefone'}
                  </div>
                  <div style={{ color: '#8ba5c4', fontSize: '12px' }}>Hotel: {r.hotel || '—'}</div>
                  {r.items && <div style={{ color: '#8ba5c4', fontSize: '12px' }}>{r.items}</div>}
                  {canRelease ? (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button
                        type="button"
                        disabled={deciding === r.id}
                        onClick={() => decideRelease(r.id, 'approve')}
                        style={{ flex: 1, background: '#62DEC2', color: '#0a1f3c', border: 'none', borderRadius: '6px', padding: '6px', fontWeight: 700, cursor: 'pointer' }}
                      >
                        Liberar cartão
                      </button>
                      <button
                        type="button"
                        disabled={deciding === r.id}
                        onClick={() => decideRelease(r.id, 'reject')}
                        style={{ flex: 1, background: 'transparent', color: '#fca5a5', border: '1px solid #fca5a5', borderRadius: '6px', padding: '6px', fontWeight: 700, cursor: 'pointer' }}
                      >
                        Recusar
                      </button>
                    </div>
                  ) : (
                    <div style={{ color: '#6b87a8', fontSize: '11px', marginTop: '6px' }}>Só administrador ou gerente decide.</div>
                  )}
                </div>
              ))}
              <div style={{ padding: '8px 12px', color: '#8ba5c4', fontSize: '11px', borderBottom: '1px solid #1e3a5f' }}>
                O cartão fica bloqueado até a decisão. O cliente pode pagar no Pix a qualquer momento. Se quiser, fale com ele antes de decidir.
              </div>
            </>
          )}

          {review.length > 0 && (
            <>
              <div style={sectionTitle('', '#fcd34d')}>🔎 Compras para revisar</div>
              {review.map(p => (
                <div key={`${p.sale_id}-${p.tour_date}-${p.tour_name}`} style={{ padding: '10px 12px', borderBottom: '1px solid #16304f' }}>
                  <div style={{ fontWeight: 600 }}>{p.customer_name || 'Cliente não identificado'}</div>
                  <div style={{ color: '#8ba5c4' }}>
                    {p.sale_reference} · {brl(p.total)} · {p.people} pessoa(s)
                  </div>
                  <div style={{ color: '#fcd34d' }}>
                    {p.tour_name || 'Tour'} em {fmtDate(p.tour_date) || '—'} · comprado {fmtDateTime(p.purchased_at)}
                  </div>
                  <div style={{ color: '#8ba5c4', fontSize: '12px' }}>
                    {p.email || 'sem e-mail'} · {p.phone || 'sem telefone'}
                  </div>
                  <div style={{ color: '#8ba5c4', fontSize: '12px' }}>Hotel: {p.hotel || '—'}</div>
                </div>
              ))}
              <div style={{ padding: '8px 12px', color: '#8ba5c4', fontSize: '11px', borderBottom: '1px solid #1e3a5f' }}>
                Site, cartão, a partir de R$ 1.000, compradas até 2 dias antes do passeio (últimos 14 dias). Só alerta: confira os dados e, se algo parecer estranho, valide o contato antes do passeio.
              </div>
            </>
          )}

          <div style={sectionTitle('', blocked24 > 0 ? '#fcd34d' : '#62DEC2')}>🛡️ Antifraude (cartão e cadastro)</div>
          {af ? (
            <>
              <div style={{ display: 'flex', gap: '8px', padding: '10px 12px', borderBottom: '1px solid #16304f' }}>
                {[
                  { label: 'Últimas 24h', d: d24 },
                  { label: 'Últimos 7 dias', d: d7 },
                ].map(({ label, d }) => (
                  <div key={label} style={{ flex: 1, background: '#0b1a30', borderRadius: '8px', padding: '8px' }}>
                    <div style={{ color: '#8ba5c4', fontSize: '11px' }}>{label}</div>
                    <div style={{ marginTop: '2px' }}>
                      <span style={{ color: '#62DEC2', fontWeight: 700 }}>{d?.allowed ?? 0}</span> liberadas
                    </div>
                    <div>
                      <span style={{ color: (d?.blocked ?? 0) > 0 ? '#fcd34d' : '#8ba5c4', fontWeight: 700 }}>
                        {d?.blocked ?? 0}
                      </span>{' '}
                      barradas
                    </div>
                    {(d?.approved ?? 0) + (d?.refused ?? 0) > 0 && (
                      <div style={{ color: '#6b87a8', fontSize: '11px', marginTop: '2px' }}>
                        banco: {d?.approved ?? 0} aprovadas · {d?.refused ?? 0} recusadas
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {d24?.by_front && Object.keys(d24.by_front).length > 0 && (
                <div style={{ padding: '8px 12px', color: '#8ba5c4', fontSize: '12px', borderBottom: '1px solid #16304f' }}>
                  {Object.entries(d24.by_front)
                    .map(([f, v]) => `${FRONT_LABEL[f] || f}: ${v.allowed} lib. / ${v.blocked} barr.`)
                    .join('  ·  ')}
                </div>
              )}

              {(af.turnstile ?? []).length > 0 && (
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #16304f' }}>
                  <div style={{ color: '#8ba5c4', fontSize: '11px', marginBottom: '4px' }}>
                    CAPTCHA (7 dias) — a base para decidir ligar o bloqueio
                  </div>
                  {af.turnstile.map(t => {
                    const isApp = t.front === 'app';
                    const pct = t.total > 0 ? Math.round((t.ok / t.total) * 100) : 0;
                    const tone = isApp ? '#8ba5c4' : pct >= 95 ? '#62DEC2' : pct >= 80 ? '#fcd34d' : '#f87171';
                    return (
                      <div key={t.front} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '1px 0' }}>
                        <span>{FRONT_LABEL[t.front] || t.front}</span>
                        <span style={{ color: tone }}>
                          {isApp
                            ? `${t.total} tentativas · sem CAPTCHA (isento)`
                            : `${t.ok}/${t.total} ok (${pct}%)${t.fail > 0 ? ` · ${t.fail} falhou` : ''}${t.skipped > 0 ? ` · ${t.skipped} sem` : ''}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {recent.length === 0 ? (
                <div style={{ padding: '10px 12px', color: '#8ba5c4' }}>Nenhuma tentativa barrada nos últimos 7 dias.</div>
              ) : (
                <>
                  <div style={{ padding: '8px 12px 2px', color: '#fcd34d', fontWeight: 600, fontSize: '12px' }}>
                    Últimas tentativas barradas
                  </div>
                  {recent.map(a => (
                    <div key={a.id} style={{ padding: '8px 12px', borderBottom: '1px solid #16304f' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: 600 }}>{FRONT_LABEL[a.front] || a.front}</span>
                        <span style={{ color: '#6b87a8', fontSize: '11px' }}>{fmtDateTime(a.created_at)}</span>
                      </div>
                      <div style={{ color: '#fcd34d' }}>{reasonText(a.reasons) || 'Limite de risco'}</div>
                      <div style={{ color: '#8ba5c4', fontSize: '12px' }}>
                        {a.email || 'sem e-mail'} · IP {a.ip || '—'}
                        {a.card ? ` · cartão ${a.card}` : ''}
                        {a.ref ? ` · ${a.ref}` : ''}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          ) : (
            <div style={{ padding: '10px 12px', color: '#8ba5c4' }}>Antifraude indisponível no momento.</div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px' }}>
            <span style={{ color: '#6b87a8', fontSize: '11px' }}>Atualiza a cada 30s</span>
            <button
              onClick={copySummary}
              style={{
                background: '#16304f',
                color: '#e6eefb',
                border: '1px solid #1e3a5f',
                borderRadius: '6px',
                padding: '4px 10px',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              {copied ? 'Copiado ✓' : 'Copiar resumo'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
